import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getArticle, updateArticle, uploadCover } from "../../../api/articles";
import type { ArticleCategory, DurationValue } from "../../../types/article";
import { DURATION_VALUES } from "../../../types/article";
import { useArticleEditor } from "../../../hooks/useArticleEditor";
import { useToast } from "@hiya/ui";
import { ArticleEditorForm } from "../../../components/articles/ArticleEditorForm";

export function EditArticlePage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { toast, showToast } = useToast();
  const { state, actions, setCoverPreview, setAdditionalBlocks } = useArticleEditor({
    requireCoverFile: false,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    getArticle(id)
      .then((article) => {
        actions.setCategory(article.category);
        const dur = DURATION_VALUES.includes(article.read_time_minutes as DurationValue)
          ? (article.read_time_minutes as DurationValue)
          : "";
        actions.setDuration(dur);
        setCoverPreview(
          article.cover_url
            ? `${article.cover_url}${article.cover_url.includes("?") ? "&" : "?"}v=${encodeURIComponent(article.updated_at)}`
            : null,
        );
        const mainBody = article.body.find((b) => b.type === "text");
        actions.setBodyText(mainBody?.content ?? "");
        actions.setHeading(article.title);
        const remaining = article.body
          .filter((b) => b !== mainBody)
          .map((b, i) => ({
            id: `existing-${i}`,
            type: b.type,
            content: b.content,
            link: b.link,
          }));
        setAdditionalBlocks(remaining);
      })
      .catch(() => showToast("Failed to load article"))
      .finally(() => setLoading(false));
  }, [id, showToast]);

  async function handlePublish() {
    if (!state.isValid || state.saving || !id) return;
    actions.setSaving(true);

    try {
      await updateArticle(id, {
        title: state.heading,
        category: state.category as ArticleCategory,
        read_time_minutes: state.duration as DurationValue,
        body: actions.buildBodyBlocks(),
      });

      if (state.coverFile) {
        await uploadCover(id, state.coverFile);
      }

      showToast("Article edited");
      navigate("/dashboard/content/articles");
    } catch {
      showToast("Failed to update article");
    } finally {
      actions.setSaving(false);
    }
  }

  if (loading) {
    return <div className="animate-pulse text-text-secondary">Loading...</div>;
  }

  return (
    <ArticleEditorForm
      title="Edit Article"
      toast={toast}
      state={state}
      actions={actions}
      onBack={() => navigate(-1)}
      onPublish={handlePublish}
    />
  );
}
